  /**
   * Types and utilities for consuming crysknife's diff JSON output.
   *
   * Diff JSON keys are DiffId::Display strings:
   *   "/<ns>/<id>" — entity (matches headJson[ns][id])
   *   "/<field>"   — field   (matches headJson[field])
   */

import {
  TreeNode,
  ObjectValue,
  NullValue,
  processKeyValue,
  type InspectorContext,
} from './inspector_tree.ts';

// ---------------------------------------------------------------------------
// Types matching crysknife's serialized Change tree
// ---------------------------------------------------------------------------

/** Leaf: entity/field was added. No value carried — look up in head JSON. */
type AddedChange = {
  diff_kind: 'Add';
};

/** Leaf: entity/field was removed. */
type RemovedChange = {
  diff_kind: 'Remove';
  base_value: unknown;
};

/** Leaf: field value changed. Fields always produce this, never Children. */
type ModifiedChange = {
  diff_kind: 'Change';
  base_value: unknown;
  head_value: unknown;
};

type DiffChildren = Record<string, ChangeNode>;

/** Intermediate: something inside changed. */
type ChildrenChange = {
  children: DiffChildren;
  /** Present when the entity's ID changed between base and head (fallback match). */
  base_path?: string;
};

type ChangeNode = AddedChange |
  RemovedChange |
  ModifiedChange |
  ChildrenChange;

/**
 * The children map consumed by buildTreeFromDiff.
 *
 * Crysknife's diff_list endpoint returns a DiffOutcome Rust enum:
 *   Same/Add/Remove → string literal
 *   Change → {"Change": {"children": {<path>: <ChangeNode>, ...}}}
 *
 * Callers must unwrap the Change envelope before passing to buildTreeFromDiff.
 */

// ---------------------------------------------------------------------------
// Public API types
// ---------------------------------------------------------------------------
export type DiffJson = DiffChildren;

export type DiffTreeData = {
  headJson: Record<string, unknown>;
  diffJson: DiffJson;
};

export type BuildDiffTreeOptions = {
  units?: string;
  includeUnchangedEntities?: boolean;
};

// ---------------------------------------------------------------------------
// Public API functions
// ---------------------------------------------------------------------------

export function normalizeDiffTreeData(
  standardizedHeadJson: Record<string, unknown>,
  rawDiffListResponse: unknown,
): DiffTreeData {
  return {
    headJson: normalizeHeadJsonForDiffTree(standardizedHeadJson),
    diffJson: normalizeDiffListJson(rawDiffListResponse),
  };
}

export function buildDiffTree(
  data: DiffTreeData,
  metadataCtx: InspectorContext,
  options: BuildDiffTreeOptions = {},
): TreeNode {
  return buildTreeFromDiff(
    data.headJson,
    data.diffJson,
    metadataCtx,
    options.units ?? 'mm',
    options.includeUnchangedEntities ?? true,
  );
}

// ---------------------------------------------------------------------------
// Normalization Helpers
// ---------------------------------------------------------------------------

/**
 * The diff walker expects top-level entity containers to be keyed by ID, but
 * some standardized head payloads still expose `{id: ...}` arrays for those
 * containers. Re-key those arrays so diff paths like `/pages/<id>` line up.
 */
function normalizeHeadJsonForDiffTree(standardizedHeadJson: Record<string, unknown>): Record<string, unknown> {
  const normalizedHeadJson: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(standardizedHeadJson)) {
    if (Array.isArray(value) && value.every((entry) => entry && typeof entry === 'object' && 'id' in entry)) {
      normalizedHeadJson[key] = Object.fromEntries(
        (value as {id: string}[]).map((entry) => [entry.id, entry]),
      );
      continue;
    }

    normalizedHeadJson[key] = value;
  }

  return normalizedHeadJson;
}

/**
 * Normalize crysknife's diff_list payload into the flat children map consumed
 * by buildTreeFromDiff.
 */
function normalizeDiffListJson(diffListResponse: unknown): DiffJson {
  return rewriteLegacyDiffNamespaces(unwrapDiffListResponse(diffListResponse));
}

function unwrapDiffListResponse(diffListResponse: unknown): DiffJson {
  if (typeof diffListResponse !== 'object' || diffListResponse === null) {
    return {};
  }

  const changeEnvelope = (diffListResponse as {Change?: {children?: DiffJson}}).Change;
  if (changeEnvelope?.children) {
    return changeEnvelope.children;
  }

  return diffListResponse as DiffJson;
}

function rewriteLegacyDiffNamespaces(diffJson: DiffJson): DiffJson {
  const normalizedDiffJson: DiffJson = {};

  for (const [key, change] of Object.entries(diffJson)) {
    normalizedDiffJson[key.replace(/^\/schematic_components\//, '/components/')] = change;
  }

  return normalizedDiffJson;
}

// ---------------------------------------------------------------------------
// Tree Building Helpers
// ---------------------------------------------------------------------------

function buildTreeFromDiff(
  headJson: Record<string, unknown>,
  diffJson: DiffJson,
  metadataCtx: InspectorContext,
  units = 'mm',
  includeUnchangedEntities = true,
): TreeNode {
  const root = makeNode('root', {level: 0});

  const changeRoot: ChildrenChange = {children: diffJson};
  buildChildrenFromHead(root, headJson, changeRoot, 1, units, false, metadataCtx, includeUnchangedEntities);

  return root;
}

function hasChangedChildren(node: ChangeNode): node is ChildrenChange {
  return 'children' in node && !('diff_kind' in node);
}

function isAdded(node: ChangeNode): node is AddedChange {
  return 'diff_kind' in node && node.diff_kind === 'Add';
}

function isRemoved(node: ChangeNode): node is RemovedChange {
  return 'diff_kind' in node && node.diff_kind === 'Remove';
}

function isModified(node: ChangeNode): node is ModifiedChange {
  return 'diff_kind' in node && node.diff_kind === 'Change';
}

/**
 * Recursively build tree children from head JSON, overlaying diff info.
 *
 * For each head key:
 *  - If the diff has any "/key/<id>" entries AND the head value is an object,
 *    treat it as an entity container and walk it via `buildEntityContainer`.
 *  - Otherwise look for a direct "/key" field-level change and dispatch on its variant (Add/Modify/Children/no-change).
 *
 * After walking head, surface diff entries with no head match: removed fields, and entire entity containers that exist only in the base.
 */
function buildChildrenFromHead(
  parent: TreeNode,
  headObj: Record<string, unknown>,
  changeNode: ChildrenChange | null,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
  includeUnchangedEntities: boolean,
): void {
  const {entityDiffs, fieldDiffs} = categorizeDiffChildren(changeNode?.children ?? {});
  const consumedNs = new Set<string>();
  const consumedFields = new Set<string>();

  addChildrenFromHeadEntries({
    parent,
    headObj,
    entityDiffs,
    fieldDiffs,
    consumedNs,
    consumedFields,
    level,
    units,
    addUnits,
    metadataCtx,
    includeUnchangedEntities,
  });

  addRemovedFieldChildren({
    parent,
    fieldDiffs,
    consumedFields,
    level,
    units,
    addUnits,
    metadataCtx,
  });

  addRemovedEntityContainers({
    parent,
    entityDiffs,
    consumedNs,
    level,
    units,
    addUnits,
    metadataCtx,
  });
}

function addChildrenFromHeadEntries({
  parent,
  headObj,
  entityDiffs,
  fieldDiffs,
  consumedNs,
  consumedFields,
  level,
  units,
  addUnits,
  metadataCtx,
  includeUnchangedEntities,
}: {
  parent: TreeNode;
  headObj: Record<string, unknown>;
  entityDiffs: Map<string, Map<string, ChangeNode>>;
  fieldDiffs: Map<string, ChangeNode>;
  consumedNs: Set<string>;
  consumedFields: Set<string>;
  level: number;
  units: string;
  addUnits: boolean;
  metadataCtx: InspectorContext;
  includeUnchangedEntities: boolean;
}): void {
  for (const [key, headVal] of Object.entries(headObj)) {
    const entityChanges = entityDiffs.get(key);
    if (entityChanges && typeof headVal === 'object' && headVal !== null && !Array.isArray(headVal)) {
      consumedNs.add(key);
      const containerNode = makeNode(key, {level});
      buildEntityContainer(
        containerNode,
        headVal as Record<string, unknown>,
        entityChanges,
        level + 1,
        units,
        addUnits || addUnitsForKey(key),
        metadataCtx,
        includeUnchangedEntities,
      );
      parent.addChild(containerNode);
      continue;
    }

    const change = fieldDiffs.get(key);
    if (change) consumedFields.add(key);

    addHeadChild(
      parent,
      key,
      headVal,
      change,
      level,
      units,
      addUnits || addUnitsForKey(key),
      metadataCtx,
      includeUnchangedEntities,
    );
  }
}

function addRemovedFieldChildren({
  parent,
  fieldDiffs,
  consumedFields,
  level,
  units,
  addUnits,
  metadataCtx,
}: {
  parent: TreeNode;
  fieldDiffs: Map<string, ChangeNode>;
  consumedFields: Set<string>;
  level: number;
  units: string;
  addUnits: boolean;
  metadataCtx: InspectorContext;
}): void {
  for (const [field, change] of fieldDiffs) {
    if (consumedFields.has(field)) continue;
    if (!isRemoved(change)) continue;

    parent.addChild(buildTreeFromValue(
      field,
      change.base_value,
      level,
      'removed',
      units,
      addUnits || addUnitsForKey(field),
      metadataCtx,
    ));
  }
}

function addRemovedEntityContainers({
  parent,
  entityDiffs,
  consumedNs,
  level,
  units,
  addUnits,
  metadataCtx,
}: {
  parent: TreeNode;
  entityDiffs: Map<string, Map<string, ChangeNode>>;
  consumedNs: Set<string>;
  level: number;
  units: string;
  addUnits: boolean;
  metadataCtx: InspectorContext;
}): void {
  for (const [ns, entityChanges] of entityDiffs) {
    if (consumedNs.has(ns)) continue;

    const containerNode = makeNode(ns, {level, removed: true});
    const childAddUnits = addUnits || addUnitsForKey(ns);

    for (const [id, change] of entityChanges) {
      addRemovedEntity(
        containerNode,
        id,
        change,
        level + 1,
        units,
        childAddUnits,
        metadataCtx,
      );
    }

    parent.addChild(containerNode);
  }
}

/**
 * Walk an entity container: head side is `Record<id, entity>`, diff side is `Map<id, ChangeNode>`. Pairs them up by id.
 */
function buildEntityContainer(
  container: TreeNode,
  headMap: Record<string, unknown>,
  entityChanges: Map<string, ChangeNode>,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
  includeUnchangedEntities: boolean,
): void {
  const childIncludeUnchangedEntities = container.key === 'pages' ? includeUnchangedEntities : true;

  if (!includeUnchangedEntities) {
    for (const [id, change] of entityChanges) {
      if (Object.hasOwn(headMap, id)) {
        addEntityChild(container, id, headMap[id], change, level, units, addUnits, metadataCtx, childIncludeUnchangedEntities);
      } else {
        addRemovedEntity(container, id, change, level, units, addUnits, metadataCtx);
      }
    }
    return;
  }

  const seen = new Set<string>();

  for (const [id, entity] of Object.entries(headMap)) {
    seen.add(id);
    const change = entityChanges.get(id);

    addEntityChild(container, id, entity, change, level, units, addUnits, metadataCtx, childIncludeUnchangedEntities);
  }

  for (const [id, change] of entityChanges) {
    if (seen.has(id)) continue;
    addRemovedEntity(container, id, change, level, units, addUnits, metadataCtx);
  }
}

/**
 * Split a flat diff children map into entity changes (`/ns/id` keys) and
 * field changes (`/field` keys) so callers can dispatch by arity.
 */
function categorizeDiffChildren(children: DiffChildren): {
  entityDiffs: Map<string, Map<string, ChangeNode>>;
  fieldDiffs: Map<string, ChangeNode>;
} {
  const entityDiffs = new Map<string, Map<string, ChangeNode>>();
  const fieldDiffs = new Map<string, ChangeNode>();

  for (const [key, change] of Object.entries(children)) {
    const parts = diffKeySegments(key);
    if (parts.length >= 2) {
      const [ns, id] = parts;
      let bucket = entityDiffs.get(ns);
      if (!bucket) {
        bucket = new Map();
        entityDiffs.set(ns, bucket);
      }
      bucket.set(id, change);
    } else if (parts.length === 1) {
      fieldDiffs.set(parts[0], change);
    }
  }
  return {entityDiffs, fieldDiffs};
}

function addHeadChild(
  parent: TreeNode,
  key: string,
  headValue: unknown,
  change: ChangeNode | undefined,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
  includeUnchangedEntities: boolean,
): void {
  if (!change) {
    parent.addChild(buildTreeFromValue(
      key, headValue, level, 'unchanged',
      units, addUnits, metadataCtx,
    ));
    return;
  }

  if (isAdded(change)) {
    parent.addChild(buildTreeFromValue(
      key, headValue, level, 'added',
      units, addUnits, metadataCtx,
    ));
    return;
  }

  if (isModified(change)) {
    parent.addChild(buildModifiedFieldNode(
      key, change, level, units, addUnits, metadataCtx,
    ));
    return;
  }

  if (hasChangedChildren(change)) {
    parent.addChild(buildIntermediateNode(
      key, headValue, change, level, units, addUnits, metadataCtx, includeUnchangedEntities,
    ));
  }
}

function addEntityChild(
  container: TreeNode,
  id: string,
  entity: unknown,
  change: ChangeNode | undefined,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
  includeUnchangedEntities: boolean,
): void {
  if (!change) {
    container.addChild(buildTreeFromValue(
      id, entity, level, 'unchanged', units, addUnits, metadataCtx,
    ));
    return;
  }

  if (isAdded(change)) {
    container.addChild(buildTreeFromValue(
      id, entity, level, 'added', units, addUnits, metadataCtx,
    ));
    return;
  }

  if (hasChangedChildren(change)) {
    container.addChild(buildIntermediateNode(
      id, entity, change, level, units, addUnits, metadataCtx, includeUnchangedEntities,
    ));
    return;
  }

  if (isModified(change)) {
    // Rare for entities (usually fields), but handle defensively.
    container.addChild(buildModifiedFieldNode(
      id, change, level, units, addUnits, metadataCtx,
    ));
  }
}

/**
 * Add a single removed entity to a parent node. This change can be:
 *  - RemovedChange: leaf - build a removed-status subtree from base_value.
 *  - ChildrenChange: entity exists in base but not head, with structured
 *    children. Recurse into buildChildrenFromHead with an empty head object.
 */
function addRemovedEntity(
  parent: TreeNode,
  id: string,
  change: ChangeNode,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
): void {
  if (isRemoved(change)) {
    parent.addChild(buildTreeFromValue(
      id, change.base_value, level, 'removed',
      units, addUnits, metadataCtx,
    ));
  } else if (hasChangedChildren(change)) {
    const node = makeNode(id, {level, removed: true});
    buildChildrenFromHead(node, {}, change, level + 1, units, addUnits, metadataCtx, true);
    parent.addChild(node);
  }
}

/**
 * Build a node for a Modified field (leaf change with base_value + head_value).
 * Dispatches to object or primitive handler based on value types.
 */
function buildModifiedFieldNode(
  key: string,
  change: ModifiedChange,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
): TreeNode {
  const {base_value, head_value} = change;

  if (typeof base_value === 'object' && base_value !== null &&
      typeof head_value === 'object' && head_value !== null) {
    return buildModifiedObjectNode(
      key, base_value as Record<string, unknown>,
      head_value as Record<string, unknown>,
      level, units, addUnits, metadataCtx,
    );
  }

  return buildModifiedPrimitiveNode(key, base_value, head_value, level, units, addUnits, metadataCtx);
}

/**
 * Build a node for an object-valued Modified change, showing per-field diffs.
 */
function buildModifiedObjectNode(
  key: string,
  baseObj: Record<string, unknown>,
  headObj: Record<string, unknown>,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
): TreeNode {
  const treeNode = makeNode(key, {level});

  const allKeys = new Set([
    ...Object.keys(baseObj),
    ...Object.keys(headObj),
  ]);

  for (const childKey of allKeys) {
    const baseChild = baseObj[childKey];
    const headChild = headObj[childKey];

    if (baseChild === undefined) {
      treeNode.addChild(buildTreeFromValue(
        childKey, headChild, level + 1, 'added',
        units, addUnits || addUnitsForKey(childKey), metadataCtx,
      ));
    } else if (headChild === undefined) {
      treeNode.addChild(buildTreeFromValue(
        childKey, baseChild, level + 1, 'removed',
        units, addUnits || addUnitsForKey(childKey), metadataCtx,
      ));
    } else {
      const headKV = processKeyValue(childKey, headChild, metadataCtx);
      const baseKV = processKeyValue(childKey, baseChild, metadataCtx);
      const modified = JSON.stringify(headChild) !== JSON.stringify(baseChild);
      treeNode.addChild(makeNode(headKV.key, {
        head: headKV.value.withUnits(addUnits || addUnitsForKey(childKey) ? units : ''),
        base: baseKV.value.withUnits(addUnits || addUnitsForKey(childKey) ? units : ''),
        primitive: true,
        level: level + 1,
        modified,
      }));
    }
  }

  return treeNode;
}

/**
 * Build a node for a primitive-valued Modified change.
 */
function buildModifiedPrimitiveNode(
  key: string,
  baseValue: unknown,
  headValue: unknown,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
): TreeNode {
  const headKV = processKeyValue(key, headValue, metadataCtx);
  const baseKV = processKeyValue(key, baseValue, metadataCtx);

  return makeNode(headKV.key, {
    head: headKV.value.withUnits(addUnits ? units : ''),
    base: baseKV.value.withUnits(addUnits ? units : ''),
    primitive: true,
    level,
    modified: true,
  });
}

/**
 * Build a node for an intermediate change (has children, something inside changed).
 */
function buildIntermediateNode(
  key: string,
  headVal: unknown,
  change: ChildrenChange,
  level: number,
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
  includeUnchangedEntities: boolean,
): TreeNode {
  const treeNode = makeNode(key, {level});

  if (typeof headVal === 'object' && headVal !== null) {
    buildChildrenFromHead(
      treeNode,
      headVal as Record<string, unknown>,
      change,
      level + 1,
      units,
      addUnits || addUnitsForKey(key),
      metadataCtx,
      includeUnchangedEntities,
    );
  }

  return treeNode;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const UNIT_KEYS = new Set([
  'points', 'text_height', 'text_width',
  'linewidth', 'line_width',
  'offset', 'offset_from_hole', 'top_size', 'hole_size',
  'diameter', 'radius', 'vertices', 'position',
]);

function addUnitsForKey(key: string): boolean {
  return UNIT_KEYS.has(key);
}

/**
 * Split a diff key into its path segments.
 *  "/components/abc123" → ["components", "abc123"] (entity, 2 segments)
 *  "/value" → ["value"] (field, 1 segment)
 */
function diffKeySegments(key: string): string[] {
  return key.split('/').filter(Boolean);
}

/**
 * Helper to create TreeNode instances with an options object instead of 11
 * positional arguments. Unspecified flags default to false / empty.
 */
function makeNode(key: string, opts: {
  head?: ObjectValue;
  base?: ObjectValue;
  primitive?: boolean;
  level: number;
  added?: boolean;
  modified?: boolean;
  removed?: boolean;
}): TreeNode {
  return new TreeNode(
    key,
    opts.head ?? new ObjectValue(null),
    opts.base ?? new ObjectValue(null),
    opts.primitive ?? false,
    !(opts.primitive ?? false),
    opts.level,
    '',
    false,
    opts.added ?? false,
    opts.modified ?? false,
    opts.removed ?? false,
  );
}

/**
 * Build a TreeNode subtree from a value. Every node in the subtree inherits
 * the same diff status: 'unchanged', 'added', or 'removed'.
 *
 * For 'unchanged' nodes, head and base are identical.
 * For 'added' nodes, base is NullValue.
 * For 'removed' nodes, head is NullValue.
 */
function buildTreeFromValue(
  key: string,
  value: unknown,
  level: number,
  status: 'unchanged' | 'added' | 'removed',
  units: string,
  addUnits: boolean,
  metadataCtx: InspectorContext,
): TreeNode {
  const isAdded = status === 'added';
  const isRemoved = status === 'removed';

  if (value === null || value === undefined) {
    return makeNode(key, {
      head: new NullValue(), base: new NullValue(),
      primitive: true, level, added: isAdded, removed: isRemoved,
    });
  }

  if (typeof value !== 'object') {
    const kv = processKeyValue(key, value, metadataCtx);
    const headVal = isRemoved ? new NullValue() : kv.value;
    const baseVal = isAdded ? new NullValue() : kv.value;

    return makeNode(kv.key, {
      head: headVal.withUnits(addUnits ? units : ''),
      base: baseVal.withUnits(addUnits ? units : ''),
      primitive: true, level, added: isAdded, removed: isRemoved,
    });
  }

  const treeNode = makeNode(key, {level, added: isAdded, removed: isRemoved});

  for (const [childKey, childVal] of Object.entries(value)) {
    treeNode.addChild(buildTreeFromValue(
      childKey, childVal, level + 1, status,
      units, addUnits || addUnitsForKey(childKey),
      metadataCtx,
    ));
  }

  return treeNode;
}
